import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { dispatchAppEvent } from 'src/common/events/appEvents';
import { MeetingNote, MeetingNoteStatus } from 'src/meeting-note/types';

export { MeetingNoteStatus } from 'src/meeting-note/types';

type UseCalendarEventMeetingNoteResult = {
  meetingNote: MeetingNote | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => void;
};

export const useCalendarEventMeetingNote = (
  eventId: string,
  { enabled }: { enabled: boolean },
): UseCalendarEventMeetingNoteResult => {
  const { data, isLoading, isError, error, refetch } = useQuery<MeetingNote>({
    queryKey: ['calendar-events', eventId, 'meeting-notes'],
    enabled,
    retry: false,
    refetchInterval: query => {
      const backgroundProcessStatus = query.state.data?.backgroundProcess?.status;
      if (backgroundProcessStatus === 'PENDING' || backgroundProcessStatus === 'PROCESSING') {
        return 2000;
      }
      const meetingNoteStatus = query.state.data?.status;
      if (meetingNoteStatus === MeetingNoteStatus.NOT_CREATED) {
        return 5000;
      }
      return false;
    },
  });

  const prevStatus = useRef(data?.backgroundProcess?.status);
  useEffect(() => {
    const status = data?.backgroundProcess?.status;
    if (status === 'SUCCESS' && prevStatus.current !== 'SUCCESS') {
      dispatchAppEvent('MEETING_NOTE_GENERATED');
    }
    prevStatus.current = status;
  }, [data?.backgroundProcess?.status]);

  return {
    meetingNote: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};
